import { useState } from 'react';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { logError } from 'src/_shared/logger';
import { useStore } from 'src/contexts/StoreContext';
import { useDebounceCallback } from 'src/hooks/useDebounceCallback';
import { DYNAMIC_MODAL_VIEWS } from 'src/store/_models/dynamic-modal-layer/dynamicModalLayer.constants';
import { ProductModelInstance } from 'src/store/_models/product';

/**
 * useGridItemContentHeaderLogic
 *
 * Handles hover state for the action icons in GridItemContentHeader.
 * The actual fade animations are handled by FadeIn/FadeOut in the component.
 */
export const useGridItemContentFooterLogic = (product: ProductModelInstance) => {
  const [productCount, setProductCount] = useState(product.quantity);
  const {
    project: { switchToGroup, activeProject },
    dynamicModal,
  } = useStore();

  const updateProductQuantitySDKAPIDebounced = useDebounceCallback(async (newQuantity: number) => {
    try {
      const finalQuantity = await activeProject?.updateProductQuantity(product, newQuantity);
      setProductCount(finalQuantity);
    } catch (error) {
      logError(error);
    }

    return newQuantity;
  }, 500);

  const addProduct = () => {
    setProductCount(count => {
      const newCount = count + 1;
      amplitude.trackEvent(AnalyticsEvents.personalize.modifyNCopyClick, {
        action: 'plus',
        current_n_copy: newCount,
      });
      updateProductQuantitySDKAPIDebounced(newCount);
      return newCount;
    });
  };
  const subtractProduct = () => {
    setProductCount(count => {
      const newCount = count - 1;
      amplitude.trackEvent(AnalyticsEvents.personalize.modifyNCopyClick, {
        action: 'minus',
        current_n_copy: newCount,
      });
      updateProductQuantitySDKAPIDebounced(newCount);
      return newCount;
    });
  };
  const removeProduct = () => {
    dynamicModal.openModal({
      dynamicModalView: DYNAMIC_MODAL_VIEWS.DeleteAll,
      onPressDeleteProduct: () => {
        amplitude.trackEvent(AnalyticsEvents.personalize.modifyNCopyClick, {
          action: 'minus',
          current_n_copy: 0,
        });
        try {
          // if is last element of the group and there are other available groups, switch to the first available
          const isLastElement = activeProject?.groups.find(g => g.id === product.groupId)?.products.length === 1;
          const otherGroups = activeProject?.filteredGroups.filter(g => g.id !== product.groupId);
          if (isLastElement && otherGroups && otherGroups.length > 0) {
            const firstFilledGroup = otherGroups[0];
            switchToGroup({ groupId: firstFilledGroup.id, scrollTo: 'top' });
          }
        } catch (error) {
          logError(error);
        }
      },
      productToDelete: product,
    });
  };

  return {
    addProduct,
    subtractProduct,
    removeProduct,
    productCount,
  };
};
